/**
 * IBAN — BICIMLENDIRME, MASKELEME ve DOGRULAMA. Tek yer.
 *
 * NEDEN TEK YER: IBAN bu urundeki en hassas alan. Paranin gittigi yer o; yanlis
 * bir karakter parayi baskasina gonderir. Ekranda bicimlendirme, listede
 * maskeleme ve formda dogrulama ayri ayri yazilsaydi uc farkli "IBAN nedir"
 * tanimi olusurdu ve biri digerinin kabul ettigi bir degeri reddederdi.
 *
 * MASKELEME BIR URUN KARARI: liste satirlari IBAN'in TAMAMINI GOSTERMEZ.
 * Sunucu, talep/teklif govdelerinde IBAN'i zaten asgariye indiriyor; istemcinin
 * bunu geri acmasi o kazanci silerdi. Tam deger yalniz kullanici acikca
 * "goster" dediginde ya da kopyalandiginda ortaya cikar.
 *
 * Bu dosya HICBIR SEY LOGLAMAZ ve IBAN'i sorgu dizesine koyacak bir yardimci
 * icermez — ikisi de bu degerin sizabilecegi klasik yollar.
 */

/** TR IBAN uzunlugu: 'TR' + 24 hane = 26 karakter. */
export const TR_IBAN_LENGTH = 26;

/**
 * Girdiyi karsilastirilabilir hale getirir: bosluklar atilir, harfler buyutulur.
 *
 * `toUpperCase()` (yerelli `toLocaleUpperCase` DEGIL) bilincli: yerelli surum
 * Turkce'de 'i' -> 'İ' uretir ve 'tr' ile baslayan bir IBAN 'TR' yerine 'TR'
 * olmayan bir seye donusurdu. IBAN alfabesi ASCII; yerelden bagimsiz donusum
 * dogru olan.
 */
export const normalizeIban = (value: string): string =>
  value.replace(/[\s-]/g, '').toUpperCase();

/**
 * Goruntuleme bicimi: dortlu gruplar. ('TR330006...' -> 'TR33 0006 ...')
 * Girdi ne olursa olsun once normalize edilir; yarim yazilmis bir deger de
 * gruplanir (formda yazarken canli bicimlendirme icin gerekli).
 */
export const formatIban = (value: string): string => {
  const normalized = normalizeIban(value);
  const groups = normalized.match(/.{1,4}/g);
  return groups ? groups.join(' ') : '';
};

/**
 * Liste satirlari icin maskeli bicim: ulke kodu + ilk dort hane, sonra gizli
 * bloklar, sonra SON DORT hane.
 *
 * Bas ve son bilincli olarak aciktir: kullanicinin kendi hesaplarini birbirinden
 * ayirabilmesi gerekiyor, yoksa maskeleme ekrani kullanilamaz hale getirir.
 * Ortadaki banka/sube/hesap hanesi — tek basina bir odeme yapmaya yeten kisim —
 * gizli kalir.
 *
 * Cok kisa (bozuk) bir degerde maskeleme YAPILMAZ, deger oldugu gibi doner:
 * yarim bir IBAN zaten islevsiz ve maskelenmis hali kullaniciya bozuklugu
 * gizlerdi.
 */
export const maskIban = (value: string): string => {
  const normalized = normalizeIban(value);
  if (normalized.length < 12) return normalized;
  const head = normalized.slice(0, 6);
  const tail = normalized.slice(-4);
  const hiddenCount = normalized.length - head.length - tail.length;
  // Gizli kisim GERCEK uzunlugu kadar degil, sabit uc blok halinde cizilir:
  // uzunluk da bir ipucu ve satirin genisligi hesaptan hesaba degismemeli.
  const hidden = '•••• •••• ••••';
  return hiddenCount > 0 ? `${formatIban(head)} ${hidden} ${tail}` : formatIban(normalized);
};

/** Harf -> sayi (A=10 … Z=35). IBAN mod-97 hesabinin gerektirdigi donusum. */
const letterToDigits = (character: string): string => {
  const code = character.charCodeAt(0);
  // 'A' (65) .. 'Z' (90)
  if (code >= 65 && code <= 90) return String(code - 55);
  return character;
};

/**
 * ISO 13616 mod-97 saglamasi.
 *
 * Sayi 30 haneyi asiyor, yani `Number` ile hesaplanamaz (2^53 ustunde sessizce
 * yanlis sonuc verir — bu tam olarak "gecerli IBAN'i reddeden / gecersizi kabul
 * eden" hata sinifi). BigInt yerine PARCALI kalan aliniyor: hem daha hizli hem
 * de her ortamda ayni.
 */
const mod97 = (digits: string): number => {
  let remainder = 0;
  for (const character of digits) {
    const digit = character.charCodeAt(0) - 48; // '0' = 48
    if (digit < 0 || digit > 9) return Number.NaN;
    remainder = (remainder * 10 + digit) % 97;
  }
  return remainder;
};

/**
 * TR IBAN dogrulamasi — SEKIL ve SAGLAMA birlikte.
 *
 * Yalniz sekil (TR + 24 hane) bakan bir kontrol, tek hane yanlis yazilmis bir
 * IBAN'i kabul eder; para o zaman ya baska bir hesaba gider ya da havale
 * reddedilir. Saglama basamagi bu yazim hatalarinin buyuk cogunlugunu yakalar
 * ve maliyeti bir dongu.
 */
export const isValidTrIban = (value: string): boolean => {
  const normalized = normalizeIban(value);
  if (!/^TR\d{24}$/.test(normalized)) return false;
  // Ilk dort karakter sona tasinir, harfler sayiya cevrilir, kalan 1 olmali.
  const rearranged = normalized.slice(4) + normalized.slice(0, 4);
  const digits = [...rearranged].map(letterToDigits).join('');
  return mod97(digits) === 1;
};
